import React, { useEffect, useMemo, useRef } from "react";

import { Formik } from "formik";
import { FormattedMessage, useIntl } from "gatsby-plugin-react-intl";
import PropTypes from "prop-types";
import Form from "react-bootstrap/Form";
import { useSelector } from "react-redux";

import RegistrationDialogStepSignUpError from "./registrationDialogStepSignUpError";
import {
  INVALID_VERIFICATION_CODE,
  useSetIdentityMutation,
  useSubmitSspUserMutation,
} from "../../../features/io/ioSspApiSlice";
import errorHelper from "../../../hooks/errorHelper";
import useSignUpPasswordResponseHelper from "../../../hooks/useSignUpPasswordResponseHelper";
import {
  ReactComponent as IconDualTonePostboxPrimary48,
} from "../../../icons/icon/48/a-icon-dualtone-postbox-primary-48.svg";
import { notificationService } from "../../../services/notification.service";
import DataLayer from "../../../utils/dataLayer";
import ScrollToFieldError from "../../../utils/formik/scrollToFieldError";
import getAccountSignUpErrorCode from "../../../utils/getAccountSignUpErrorCode";
import SspDataHelper from "../../accountSignup/sspDataHelper";
import IconButton from "../../button/iconButton";
import TimeoutButton from "../../button/timeoutButton";
import CodeVerification from "../../codeVerification/codeVerification";
import RegistrationDialogHeadline from "../registrationDialogHeadline";

const RegistrationDialogStepCodeVerification = ({ content }) => {
  const registrationData = useSelector((state) => state.registration.registrationData);
  const sspRegistrationData = useSelector((state) => state.sspRegistration.sspRegistrationData);
  const formRef = useRef();
  const intl = useIntl();
  const validationSchema = useMemo(
    () => SspDataHelper.getCodeVerificationValidationSchema(intl),
    [],
  );

  const [setIdentity, setIdentityResult] = useSetIdentityMutation();
  const [submitSspUser, submitSspUserResult] = useSubmitSspUserMutation();
  const error = errorHelper(setIdentityResult.error);
  const salesForceError = error.status === 422
    && setIdentityResult.error.data.detail?.error?.error_code;
  const formDisabled = setIdentityResult.isLoading || salesForceError;

  useSignUpPasswordResponseHelper(submitSspUserResult);

  useEffect(() => {
    if (setIdentityResult.isSuccess) {
      DataLayer.pushEvent("pp_registration_signup_email_verification", { event_value: "success" });
      window.skipBeforeUnload = true;
      window.location.href = content.successPage;
    }

    if (setIdentityResult.isError) {
      DataLayer.pushEvent("pp_registration_signup_email_verification", { event_value: "fail" });

      if (setIdentityResult.error === INVALID_VERIFICATION_CODE) {
        formRef.current.setErrors({ code: intl.formatMessage({ id: "error_signup.wrong_code" }) });
      } else if (!salesForceError) {
        const signUpError = getAccountSignUpErrorCode(
          error,
          intl,
        );

        notificationService.error(signUpError.errorMessage,
          {
            action: signUpError.action,
          });
      }
    }
  }, [setIdentityResult]);

  if (salesForceError) {
    return (
      <RegistrationDialogStepSignUpError
        error={setIdentityResult.error.data.detail?.error?.error_code}
        modalLeaveUrl={content.successPageWithoutCreateAccount}
      />
    );
  }

  return (
    <>
      <IconDualTonePostboxPrimary48 className="mb-4" />
      <RegistrationDialogHeadline
        headline={intl.formatMessage({ id: "registration.code_verification.title" })}
        subheadline={intl.formatMessage({ id: "registration.code_verification.subtitle" }, { email: registrationData.emailAddress })}
      />
      <Formik
        innerRef={(f) => {
          formRef.current = f;
        }}
        initialValues={registrationData}
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={(values) => {
          setIdentity(SspDataHelper.preparePayloadForSetIdentity(sspRegistrationData, values.code));
        }}
      >
        {({
          handleSubmit,
        }) => (
          <Form onSubmit={handleSubmit}>

            <CodeVerification
              name="code"
              numberOfFields={6}
              regex={/^[0-9]/}
              buttons={[
                <IconButton
                  key="verify"
                  type="submit"
                  disabled={formDisabled}
                  loadingWithPreText={setIdentityResult.isLoading}
                >
                  {setIdentityResult.isLoading
                    ? <FormattedMessage id="registration.is_checked" />
                    : <FormattedMessage id="registration.verify_email" />}
                </IconButton>,
                <TimeoutButton
                  key="new_code"
                  onClick={() => {
                    submitSspUser(
                      SspDataHelper.preparePayloadForSubmitUser(sspRegistrationData),
                    );
                  }}
                  intlId="registration.new_code"
                  disabled={formDisabled}
                  variant="inverted-primary"
                />,
              ]}
              disabled={formDisabled}
            />

            <ScrollToFieldError />
          </Form>
        )}
      </Formik>
    </>
  );
};

RegistrationDialogStepCodeVerification.stepName = "registration_signup_email_verification";

RegistrationDialogStepCodeVerification.propTypes = {
  content: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default RegistrationDialogStepCodeVerification;
